import React, { type FC, useCallback, useState } from 'react'
import { Box, type ButtonProps, type SxProps, type Theme } from '@mui/material'
import { Add, Warning } from '@mui/icons-material'

import { type ConnectwareError, selectExplorerDoesTopicExist, Translation } from '../../../../domain'

import { createOnEnterHandler } from '../..'
import { Button, TextField, useAnchorCloser } from '../../common'
import { FormattedTranslation } from '../../Internationalization'
import { useAppState, useAppUsecase } from '../../State'
import { PopoverButton } from './PopoverButton'

const wrapperStlye: SxProps<Theme> = { display: 'flex' }
const buttonStyle: SxProps<Theme> = { ml: 1 }

const AddTopicPopover: FC = () => {
    const close = useAnchorCloser()

    const manage = useAppUsecase('explorerTopicManagementUsecase')
    const [[newTopic, error], setState] = useState<[newTopic: string, error: ConnectwareError | null]>(['', null])
    const doesTopicExist = useAppState((s) => selectExplorerDoesTopicExist(s, newTopic), [newTopic])

    const create = useCallback((): void => {
        try {
            manage.addCustomTopic(newTopic)
            close()
        } catch (e) {
            setState(([topic]) => [topic, e as ConnectwareError])
        }
    }, [manage, newTopic])

    const dynamicProps: ButtonProps = error ? { variant: 'danger', startIcon: <Warning fontSize="small" />, title: error.message } : {}

    return (
        <Box sx={wrapperStlye}>
            <TextField
                autoFocus
                value={newTopic}
                onChange={({ currentTarget: { value } }) => setState([value, null])}
                onKeyUp={!doesTopicExist ? createOnEnterHandler(create) : undefined}
            />
            <Button sx={buttonStyle} size="small" variant="primary" disabled={doesTopicExist} {...dynamicProps} onClick={create}>
                <FormattedTranslation id={Translation.ADD_CUSTOM_TOPIC} />
            </Button>
        </Box>
    )
}

export const AddTopicButton: FC = () => (
    <PopoverButton data-testid="addTopic" startIcon={<Add />} text={Translation.ADD_CUSTOM_TOPIC} popover={<AddTopicPopover />} />
)
